import * as z from "zod";

export const VoiceModeSchema = z.enum(["realtime", "transcription"]);
export type VoiceMode = z.infer<typeof VoiceModeSchema>;
export const PlatformVoiceConfigurationSchema = z.object({
  enabled: z.boolean(),
  provider: z.literal("bailian"),
  endpoint: z.string().trim().min(1).max(2048),
  modelId: z.string().trim().min(1).max(200),
  /** Legacy field retained so old rows remain readable; realtime no longer uses it to answer. */
  asrModelId: z.string().trim().min(1).max(200).optional(),
  /** Realtime synthesis model. Required by the new Bot-backed call flow. */
  ttsModelId: z.string().trim().min(1).max(200).optional(),
  replySource: z.literal("bot").optional(),
  voice: z.string().trim().min(1).max(200),
  turnDetection: z.enum(["semantic", "activity"]),
  polish: z.boolean(),
});
export type PlatformVoiceConfiguration = z.infer<typeof PlatformVoiceConfigurationSchema>;
export const PlatformVoiceEntrySchema = z.object({
  mode: VoiceModeSchema,
  version: z.number().int().nonnegative(),
  hasKey: z.boolean(),
  configuration: PlatformVoiceConfigurationSchema,
});
export type PlatformVoiceEntry = z.infer<typeof PlatformVoiceEntrySchema>;
export const PlatformVoiceInputSchema = PlatformVoiceEntrySchema.omit({
  version: true,
  hasKey: true,
}).extend({
  expectedVersion: z.number().int().nonnegative(),
  apiKey: z.string().trim().max(4096).optional(),
});
export const PlatformVoiceStatusSchema = z.object({
  managed: z.boolean(),
  realtime: z.boolean(),
  transcription: z.boolean(),
});
export const VoiceStreamStartSchema = z.object({
  mode: VoiceModeSchema,
  botId: z.string().min(1).max(200),
});
export const VoiceStreamTicketSchema = z.object({
  ticket: z.string(),
  path: z.literal("/api/voice/stream"),
});
export const VoiceStreamClientEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("authenticate"),
    version: z.literal(1),
    ticket: z.string().min(32).max(128),
  }),
  z.object({
    type: z.literal("audio"),
    captureSeq: z.number().int().positive().max(1000000).optional(),
    audio: z
      .string()
      .min(4)
      .max(44000)
      .regex(/^[A-Za-z0-9+/]+={0,2}$/),
  }),
  z.object({ type: z.literal("finishInput") }),
  z.object({ type: z.literal("playbackDone"), responseId: z.string().min(1).max(200) }),
  z.object({ type: z.literal("close") }),
]);
export const VoiceStreamEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ready"), inputAcknowledgements: z.boolean().optional() }),
  z.object({ type: z.literal("inputAck"), captureSeq: z.number().int().positive() }),
  z.object({
    type: z.literal("text"),
    itemId: z.string(),
    responseId: z.string().optional(),
    role: z.enum(["user", "assistant"]),
    text: z.string().max(20000),
    final: z.boolean(),
  }),
  z.object({ type: z.literal("audio"), responseId: z.string(), audio: z.string().max(1000000) }),
  z.object({ type: z.literal("interrupt"), responseId: z.string() }),
  z.object({ type: z.literal("responseEnd"), responseId: z.string() }),
  z.object({ type: z.literal("done"), text: z.string().max(20000).optional() }),
  z.object({ type: z.literal("error"), message: z.string().max(200) }),
]);
export type VoiceStreamEvent = z.infer<typeof VoiceStreamEventSchema>;
export type VoiceStreamClientEvent = z.infer<typeof VoiceStreamClientEventSchema>;
